import { ChangeEventHandler, useEffect, useState } from 'react';
import {
  Grid, Paper, Stack, TextField, Typography,
} from '@mui/material';
import { v4 as uuid } from 'uuid';
import BlockProps from '../../../models/BlockProps';
import BlockHtmlSafeData from './BlockHtmlSafeData';
import Utils from '../../../utils/Utils';
import CloseButton from '../../Buttons/Close';
import EditableText from '../../Form/EditableText';
import { stripScripts, containsJavaScript } from '../../../utils/htmlSanitization';

function BlockHtmlSafe({
  data, isEditing, isTemplate, editHandler,
}: BlockProps) {
  const props = (data.data as BlockHtmlSafeData);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (props.html?.value && containsJavaScript(props.html.value)) {
      setValidationError('JavaScriptは許可されていません。scriptタグ、イベント属性(onclick等)、javascript:は使用できません。');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleHtml: ChangeEventHandler<HTMLInputElement> = (event) => {
    props.html.value = stripScripts(event.target.value);
  };

  const handleBlur: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
    const { value } = event.target;
    const sanitized = stripScripts(value);
    props.html.value = sanitized;

    if (containsJavaScript(value)) {
      setValidationError('JavaScriptが検出されたため、自動的に削除されました。');
      setTimeout(() => setValidationError(null), 3000);
    } else {
      setValidationError(null);
    }

    editHandler(data.id, props);
  };

  const handleTitleUpdate = (text: string) => {
    props.title.value = text;
    editHandler(data.id, props);
  };

  const sanitizedHtml = props.html?.value ? stripScripts(props.html.value) : '';

  return (
    <Stack
      spacing={0}
      sx={{
        padding: 1.5,
        overflow: 'hidden',
      }}
    >
      {(isEditing && !isTemplate) && (
        <Stack spacing={0} sx={{ border: '1px dashed lightGray', p: 2, m: 2 }}>
          <TextField
            sx={{
              '& textarea': {
                resize: 'vertical',
              },
            }}
            key={uuid()}
            label="埋め込みHTML"
            multiline
            rows={4}
            defaultValue={props.html?.value}
            onChange={handleHtml}
            onBlur={handleBlur}
          />
          {validationError && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {validationError}
            </Typography>
          )}
        </Stack>
      )}

      <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
        className="block-content-wrapper"
      >
        {props.title && (
          <Grid item sx={[Utils.hoverBorder(isEditing), { marginBottom: 2 }]}>
            <CloseButton
              show={isEditing}
              updateHandler={() => {
                props.title = undefined;
                editHandler(data.id, props);
              }}
            />
            <EditableText
              text={props.title.value}
              textColor={props.title.settings?.textColor}
              isEditable={isEditing && !isTemplate}
              updateHandler={handleTitleUpdate}
            />
          </Grid>
        )}
        <Grid
          container
          direction="column"
          sx={{
            justifyContent: 'center', alignItems: 'center', width: '100%', height: 'auto',
          }}
        >
          {sanitizedHtml && (
            <div
              id={`block-html-safe-${data.id}`}
              style={{ width: '100%' }}
              className="block-html-container"
              dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            />
          )}
          {!sanitizedHtml && (
            <Paper
              elevation={0}
              sx={{
                width: '100%', height: 450, background: '#F4F4F6', border: '1px dashed lightGray',
              }}
            />
          )}
        </Grid>
      </Grid>
    </Stack>
  );
}

export default BlockHtmlSafe;
